import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { ButtonPickerComponent } from '@app/shared/button-picker/button-picker.component';
import { ButtonComponent } from '@app/shared/button/button.component';
import { DropdownComponent } from '@app/shared/dropdown/dropdown.component';
import { FilterModalComponent } from '@app/shell/header/data-filter/filter-modal/filter-modal.component';
import { SelectComponent } from '@shared/modules/select/select.component';
import { DialogService } from 'primeng/dynamicdialog';

import * as sharedServices from '@shared/services';

import { Router } from '@angular/router';
import * as models from '@app/real-property-list-filter/models';
import { ResidentialComplexService } from '@app/real-property-list-filter/services';
import { SettingsService } from '@app/settings.service';
import { RealPropertyService } from '@modules/real-property-details';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { SearchModalComponent } from '@app/shell/saved-search/search-modal/search-modal.component';
import { PanelComponent } from '@app/shared/panel/panel.component';

@Component({
  selector: 'app-data-filter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonPickerComponent,
    ButtonComponent,
    DropdownComponent,
    SelectComponent,
    SearchModalComponent,
    PanelComponent,
  ],
  templateUrl: './data-filter.component.html',
  styleUrls: ['./data-filter.component.scss'],
})
export class DataFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isHomePage = true;
  @Output() searchEmit: EventEmitter<any> = new EventEmitter<any>();

  public dealTypes = models.operationOptions;
  public objectTypes$: Observable<any>;
  public objectTypesLoading$: Observable<boolean>;
  public currentRoominess$: Observable<any>;
  public currentRealPropertyCount$: Observable<number>;
  public countFilters$: Observable<number>;

  cardSelectionButtons = [
    { id: 1, label: 'На карте', icon: 'assets/images/new-design-icons/Map (white).svg', tab: 'map' },
    { id: 2, label: 'Список', icon: 'assets/images/new-design-icons/List (white).svg', tab: 'list' },
  ];
  selectedMapButtonId: number;

  form: FormGroup;

  isValidPrice: boolean;

  savedSearchFilter: any;
  showSaveSearchModal: boolean;

  isTablet$: Observable<boolean>;

  destroy$: Subject<any> = new Subject<any>();

  constructor(
    private _dialogService: DialogService,
    private _dictService: sharedServices.DictionaryService,
    private _settingsService: SettingsService,
    private _addressService: sharedServices.AddressService,
    private _complexService: ResidentialComplexService,
    private _realPropertyService: RealPropertyService,
    private _router: Router,
  ) {}

  ngOnInit() {
    this.isTablet$ = this._settingsService.isTablet();
    this.currentRealPropertyCount$ = this._realPropertyService.realPropertyTotalCountFilter$;
    this.countFilters$ = this._settingsService.countFilters$;
    this._dictService.getObjectTypes();
    this.form = new FormGroup<any>({
      dealType: new FormControl(2),
      realtyType: new FormControl(),
      priceFrom: new FormControl(),
      priceTo: new FormControl(),
      areaFrom: new FormControl(),
      areaTo: new FormControl(),
      roominess: new FormControl([]),
    });

    if (!this.isHomePage) {
      this.selectedMapButtonId = this._router.url.includes('map') ? 1 : 2;
    }

    this.objectTypes$ = this._dictService.objectTypes$.pipe(
      map((types) => {
        return types.map((cond: any) => ({ value: cond.id, label: cond.value }));
      }),
    );
    this.currentRoominess$ = this._settingsService.currentFilters$.pipe(map((f) => f.roominess));

    combineLatest([this._settingsService.currentFilters$, this._dictService.currentObjectTypeId$]).pipe(takeUntil(this.destroy$))
      .subscribe(([filter, objectTypeIds]) => {
        this.form.patchValue({
          dealType: filter.dealType.value,
          realtyType: objectTypeIds,
          priceFrom: filter.price.from,
          priceTo: filter.price.to,
          areaFrom: filter.area.from,
          areaTo: filter.area.to,
        });
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.isHomePage) {
      this.selectedMapButtonId = this._router.url.includes('map') ? 1 : 2;
    } else {
      this.selectedMapButtonId = undefined;
    }
  }

  getCountRealProperty(filters: any, objectTypeId: number, city: any, street: any, district: any, condition: any, complex: any) {
    const addressCode = street.length ? street : district.length ? district : [city.code];
    const data: any = {
      operationTypeId: filters.dealType.value,
      applicationId: filters.proposalId,
      addressCodes: addressCode,
      objectTypeId: objectTypeId,
      residentialComplexIds: complex,
      housingConditionIds: condition,
      numberOfRooms: filters.roominess,
      objectPrice: {
        from: filters.price.from,
        to: filters.price.to,
      },
      totalArea: {
        from: filters.area.from,
        to: filters.area.to,
      },
      yearOfConstruction: {
        from: filters.constructYear.from,
        to: filters.constructYear.to,
      },
    };
  }

  switchMapButton(id: number, tab: string) {
    this.selectedMapButtonId = id;
    this.searchEmit.emit(tab);
  }

  selectDealType(value: number) {
    console.log(value)
    const dealType = this.dealTypes.find((item) => item.value === value);
    this._settingsService.updateFilters({ dealType });
    this._realPropertyService.getRealPropertyCountDispatch({ operationTypeId: value });
    this._settingsService.updateFiltersCount({ dealType: value });

    console.log(this.form.value)
  }

  roomSelect(ids: number[]) {
    this._settingsService.updateFilters({ roominess: ids });
    this.form.get('roominess').setValue(ids);
    this._realPropertyService.getRealPropertyCountDispatch({ numberOfRooms: ids });
    this._settingsService.updateFiltersCount({ roominess: ids });
  }

  priceSelect() {
    const from = this.form.get('priceFrom').value;
    const to = this.form.get('priceTo').value;
    this._settingsService.updateFilters({ price: { from, to } });
    this._realPropertyService.getRealPropertyCountDispatch({ objectPrice: { from, to } });
    this._settingsService.updateFiltersCount({ priceFrom: from, priceTo: to });
  }

  areaSelect() {
    const from = this.form.get('areaFrom').value;
    const to = this.form.get('areaTo').value;
    this._settingsService.updateFilters({ area: { from, to } });
    this._realPropertyService.getRealPropertyCountDispatch({ totalArea: { from, to } });
    this._settingsService.updateFiltersCount({ areaFrom: from, areaTo: to });
  }

  selectRealtyType(id: number) {
    this._dictService.updateCurrentObjectType(id);
    this._realPropertyService.getRealPropertyCountDispatch({ objectTypeId: id });
    this._settingsService.updateFiltersCount({ realtyType: id });
  }

  public onCheckPriceValidation(): void {
    const from = this.form.get('priceFrom').value;
    const to = this.form.get('priceTo').value;
    if (to && from) {
      this.isValidPrice = +from >= +to;
    } else {
      this.isValidPrice = false;
    }
  }

  openMoreFilters() {
    const dialog = this._dialogService.open(FilterModalComponent, {
      modal: true,
      autoZIndex: false,
      showHeader: false,
      closable: true,
      styleClass: 'filter-dialog',
      data: { filters: this.form.value },
      dismissableMask: true,
    });

    dialog.onClose.subscribe(({ action }) => {
      if (action === 'search') {
        this.onSearch();
      }
      if (action === 'save') {
        this.saveSearch();
      }
    });
  }

  saveSearch() {
    combineLatest([
      this._addressService.currentCity$,
      this._addressService.currentStreets$.pipe(map((st) => st.map((s) => s.code))),
      this._addressService.currentDistricts$.pipe(map((dt) => dt.map((s: any) => s.code))),
      this._dictService.currentHouseConditions$.pipe(map((st: any[]) => st.map((s) => s.id))),
      this._complexService.currentComplex$.pipe(map((st) => st.map((s: any) => s.id))),
      this._dictService.currentObjectTypeId$,
      this._settingsService.currentFilters$,
    ])
    .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(([city, street, district, condition, complex, objectTypes, filters]) => {
        const addressCode = street.length ? street : district.length ? district : [city.code];
        const data: any = {
          pageNumber: 0,
          pageSize: 20,
          operationTypeId: filters.dealType.value,
          applicationId: filters.proposalId,
          addressCodes: addressCode,
          objectTypeId: objectTypes,
          residentialComplexIds: complex,
          encumbrance: null,
          exchange: null,
          atelier: filters.layout,
          housingConditionIds: condition,
          direction: null,
          sortBy: null,
          numberOfRooms: filters.roominess,
          moreThanFiveRooms: null,
          probabilityOfBidding: null,
          objectPrice: {
            from: filters.price.from,
            to: filters.price.to,
          },
          totalArea: {
            from: filters.area.from,
            to: filters.area.to,
          },
          yearOfConstruction: {
            from: filters.constructYear.from,
            to: filters.constructYear.to,
          },
        };
        this.saveFilterSettings(data);
      });
  }

  onSearch() {
    this.searchEmit.emit();
  }

  saveFilterSettings(filter: any): void {
    this.savedSearchFilter = filter;
    this.showSaveSearchModal = true;
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }
}
